import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Progress } from '@/components/ui/progress';
import axios from 'axios';
import { toast } from 'sonner';
import { riskProfileQuestions, calculateRiskProfile } from '@/data/riskProfileQuestions';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RiskAssessmentPage = ({ user, onLogout, clientId }) => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(clientId || '');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [riskProfiles, setRiskProfiles] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, profilesRes] = await Promise.all([
        axios.get(`${API}/clients`),
        axios.get(`${API}/risk-profiles`)
      ]);
      setClients(clientsRes.data);
      setRiskProfiles(profilesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    }
  };

  const handleAnswer = (value) => {
    const questionId = riskProfileQuestions[currentQuestion].id;
    setAnswers(prev => ({
      ...prev,
      [questionId]: { questionId, value }
    }));
  };

  const handleNext = () => {
    if (!answers[riskProfileQuestions[currentQuestion].id]) {
      toast.error('Please select an answer');
      return;
    }
    if (currentQuestion < riskProfileQuestions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      completeAssessment();
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const completeAssessment = async () => {
    if (!selectedClient) {
      toast.error('Please select a client');
      return;
    }

    const calculatedResult = calculateRiskProfile(answers);
    setResult(calculatedResult);
    
    const matchingProfile = riskProfiles.find(p => p.name === calculatedResult.profile);
    
    if (!matchingProfile) {
      toast.error('Risk profile not found');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/risk-assessments`, {
        client_id: selectedClient,
        risk_profile_id: matchingProfile.id,
        assessment_data: {
          answers,
          score: calculatedResult.score,
          profile: calculatedResult.profile
        }
      });
      
      toast.success('Risk assessment completed successfully');
      setCompleted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const progress = ((currentQuestion + 1) / riskProfileQuestions.length) * 100;
  const currentQ = riskProfileQuestions[currentQuestion];

  if (completed && result) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="max-w-3xl mx-auto space-y-6" data-testid="assessment-complete">
          <div className="text-center bg-white rounded-xl p-12 shadow-sm border border-gray-200">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <Check size={40} className="text-green-600" />
            </div>
            <h1 className="text-3xl font-semibold text-gray-900 mb-4">Assessment Complete</h1>
            <div className="space-y-4 text-left max-w-md mx-auto">
              <div className="p-4 bg-[#fffdf0] rounded-lg border border-ugmonk-border">
                <p className="text-gray-600 text-sm mb-1">Risk Profile</p>
                <p className="text-2xl font-semibold text-ugmonk-dark">{result.profile}</p>
              </div>
              <div className="p-4 bg-[#fffdf0] rounded-lg border border-ugmonk-border">
                <p className="text-gray-600 text-sm mb-1">Score</p>
                <p className="text-2xl font-semibold text-gray-900">{result.score} / {riskProfileQuestions.length * 4}</p>
              </div>
            </div>
            <Button className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black mt-8" onClick={() => window.location.href = '/risk-profiling'}>
              Back to Risk Profiling
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="risk-assessment-container">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: "'PerfectNineties', serif"}}>Risk Assessment</h1>
          <p className="text-gray-600">Complete this questionnaire to determine your risk profile</p>
        </div>

        {!selectedClient ? (
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
            <Label htmlFor="client-select" className="text-gray-700 text-lg mb-4 block">Select Client</Label>
            <select
              id="client-select"
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 text-gray-900"
              data-testid="client-select"
            >
              <option value="">Choose a client...</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.client_name} - {client.type}
                </option>
              ))}
            </select>
            <Button
              className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black w-full mt-6"
              disabled={!selectedClient}
              onClick={() => {}}
              data-testid="start-assessment-button"
            >
              Start Assessment
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Progress Bar */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-gray-600 text-sm">Question {currentQuestion + 1} of {riskProfileQuestions.length}</span>
                <span className="text-gray-600 text-sm">{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            {/* Question Card */}
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 fade-in">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">{currentQ.question}</h2>
              
              <RadioGroup
                value={answers[currentQ.id]?.value || ''}
                onValueChange={handleAnswer}
                className="space-y-3"
              >
                {currentQ.options.map((option) => (
                  <div
                    key={option.value}
                    className={`flex items-center space-x-3 p-4 rounded-lg border transition-all cursor-pointer ${
                      answers[currentQ.id]?.value === option.value
                        ? 'bg-[#fffdf0] border-[#1a1a1a]/50'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300'
                    }`}
                    onClick={() => handleAnswer(option.value)}
                  >
                    <RadioGroupItem value={option.value} id={`${currentQ.id}-${option.value}`} />
                    <Label
                      htmlFor={`${currentQ.id}-${option.value}`}
                      className="flex-1 text-gray-900 cursor-pointer text-sm leading-relaxed"
                    >
                      {option.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <Button
                onClick={handlePrevious}
                disabled={currentQuestion === 0}
                variant="outline"
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
                data-testid="previous-button"
              >
                <ChevronLeft size={18} className="mr-2" />
                Previous
              </Button>
              
              <Button
                onClick={handleNext}
                disabled={!answers[currentQ.id] || submitting}
                className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black"
                data-testid="next-button"
              >
                {currentQuestion === riskProfileQuestions.length - 1 ? (
                  submitting ? 'Submitting...' : 'Complete'
                ) : (
                  <>
                    Next
                    <ChevronRight size={18} className="ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default RiskAssessmentPage;
